<script setup lang="ts">
/**
 * The confirmation that happens WHERE YOU CLICKED (MAR-474).
 *
 * The trigger — a row, a menu item, a toolbar button — turns into its own confirmation and turns
 * back. Nothing is overlaid, nothing is focus-trapped, and above all nothing blocks the renderer:
 * `window.confirm()` freezes the whole tab, which is how a case delete on `/app/cases/[id]` read as
 * a hung app for 30+ seconds while the request was in fact running fine.
 *
 * WHY A COMPONENT AND NOT AN AWAITED `confirm({ ... })` FUNCTION. A function is a one-line diff at
 * every call site, which is genuinely tempting across ~27 of them — but a function can only ever
 * summon a NEW surface, because it has no access to the markup of the thing that called it. The
 * whole point of this pattern is that no new surface appears: the affordance you clicked is the
 * affordance that asks. That requires the trigger to live inside a slot, so a component it is.
 * (`DeleteDocumentDialog` stays a dialog and is not replaced by this — it exists to answer *which
 * expedientes reference this file*, which is a question, not a confirmation.)
 *
 * `display: contents` on the root keeps it out of the layout entirely, so it can be dropped into a
 * flex row, a table cell or a dropdown without moving anything.
 */
import { AlertTriangle, Loader2 } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  /** The question, already resolved through i18n by the caller. Short: it has one row's width. */
  question: string
  /** Defaults to `common.delete` for `destructive`, `common.confirm` otherwise. */
  confirmLabel?: string
  /** Defaults to `common.keep` for `destructive`, `common.cancel` otherwise. */
  cancelLabel?: string
  variant?: 'destructive' | 'default'
  /**
   * Optional. When given, the component runs it on confirm and owns the pending state itself —
   * which is the point for a slow action, since that spinner is the feedback MAR-474 was missing.
   * Without it the caller drives `busy` instead. `confirm` is emitted either way.
   */
  action?: () => unknown | Promise<unknown>
  /** Caller-driven pending state, for when `action` isn't used. */
  busy?: boolean
  /** Layout of the confirm strip. Defaults to a full-width row, which is what a list row wants. */
  stripClass?: string
}>(), {
  variant: 'destructive',
  busy: false,
  stripClass: 'w-full',
})

const emit = defineEmits<{ confirm: []; cancel: [] }>()

const { t } = useI18n()

const armed = ref(false)
const running = ref(false)
const root = ref<HTMLElement | null>(null)
const confirmButton = ref<HTMLButtonElement | null>(null)

const pending = computed(() => running.value || props.busy)
const destructive = computed(() => props.variant === 'destructive')
const confirmText = computed(
  () => props.confirmLabel ?? (destructive.value ? t('common.delete') : t('common.confirm')),
)
const cancelText = computed(
  () => props.cancelLabel ?? (destructive.value ? t('common.keep') : t('common.cancel')),
)

/**
 * Escape and outside-click are bound only while armed, and unbound the moment it disarms — an
 * always-on document listener per row is how a long list gets slow. There is no `watch` here on
 * purpose (house style): arming and disarming are events, so the handlers do the binding.
 */
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') cancel()
}
function onOutside(e: MouseEvent) {
  if (!root.value?.contains(e.target as Node)) cancel()
}
function listen(on: boolean) {
  // Both calls stay on `document` as method calls. Hoisting the method into a variable
  // (`const fn = on ? document.addEventListener : …`) reads as the tidier form and is a trap: the
  // receiver is lost, so the listener is registered against whatever `this` resolves to — never
  // `document` — and in a strict-mode module a browser throws `Illegal invocation` outright. Escape
  // and outside-click were silently dead until `tests/unit/inlineConfirm.test.ts` mounted the component.
  if (on) {
    document.addEventListener('keydown', onKeydown as EventListener)
    document.addEventListener('mousedown', onOutside as EventListener)
  } else {
    document.removeEventListener('keydown', onKeydown as EventListener)
    document.removeEventListener('mousedown', onOutside as EventListener)
  }
}

/**
 * Arming removes the trigger from the DOM, so focus has to be put somewhere or it falls to `body`
 * and a keyboard user loses their place mid-question.
 *
 * `confirmButton` only exists when the DEFAULT strip is rendered. A caller supplying `#confirm`
 * draws its own buttons — draft 07's two-value body, draft 05's selection bar — and there is no ref
 * to reach them by, so fall back to the first focusable thing inside the root.
 */
function focusConfirmation() {
  const fallback = () => root.value?.querySelector<HTMLElement>(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )
  ;(confirmButton.value ?? fallback())?.focus()
}

function arm() {
  if (armed.value || pending.value) return
  armed.value = true
  listen(true)
  nextTick(focusConfirmation)
}

function cancel() {
  if (!armed.value || pending.value) return
  armed.value = false
  listen(false)
  emit('cancel')
}

async function confirm() {
  if (pending.value) return
  emit('confirm')
  if (!props.action) {
    // The caller owns the outcome; close so the row goes back to being a row.
    armed.value = false
    listen(false)
    return
  }
  running.value = true
  try {
    await props.action()
  } finally {
    running.value = false
    armed.value = false
    listen(false)
  }
}

onUnmounted(() => listen(false))

/** So a parent that cancels for its own reasons (selection cleared, row removed) can say so. */
defineExpose({ arm, cancel, armed })
</script>

<template>
  <div ref="root" class="contents">
    <!-- The trigger, as the caller wrote it. `arm` is what its click handler calls. -->
    <slot v-if="!armed" :arm="arm" :armed="armed" :busy="pending" />

    <slot v-else name="confirm" :confirm="confirm" :cancel="cancel" :busy="pending">
      <div
        :class="[
          stripClass,
          'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs motion-safe:transition-colors',
          destructive
            ? 'border-destructive/40 bg-destructive/5 text-foreground'
            : 'border-input bg-muted text-foreground',
        ]"
      >
        <AlertTriangle
          class="h-3.5 w-3.5 shrink-0"
          :class="destructive ? 'text-destructive' : 'text-muted-foreground'"
        />
        <span class="min-w-0 flex-1 truncate" :title="question">{{ question }}</span>
        <button
          ref="confirmButton"
          type="button"
          :disabled="pending"
          class="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60"
          :class="destructive
            ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            : 'bg-primary text-primary-foreground hover:bg-primary/90'"
          @click="confirm"
        >
          <Loader2 v-if="pending" class="h-3 w-3 shrink-0 animate-spin" />
          {{ confirmText }}
        </button>
        <button
          type="button"
          :disabled="pending"
          class="shrink-0 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60"
          @click="cancel"
        >
          {{ cancelText }}
        </button>
      </div>
    </slot>
  </div>
</template>
